import type {
  TeamData,
  TeamRequestData,
  TeamsQueryParams
} from '@library/types/teams';
import {
  mapTeamDataToRequest as mapTeamDataToRequestUtil,
  transformTeamsData
} from '@bo-utils/teams';
import { useCallback, useMemo } from 'react';
import {
  useCreateTeamMutation,
  useGetTeamsQuery,
  useUpdateTeamMutation
} from '@bo-services/teamsApi';
import type { LeaguesList } from '@library/types/leagues';

export const useTeams = ({
  currentItemId,
  setSubmitMessage,
  teamsQueryParams
}: {
  currentItemId: string;
  leaguesList: LeaguesList;
  setSubmitMessage: (message: string) => void;
  teamsQueryParams: TeamsQueryParams;
}) => {
  const {
    data: response,
    isError,
    isLoading,
    isSuccess
  } = useGetTeamsQuery(teamsQueryParams);

  const [createTeam] = useCreateTeamMutation();
  const [updateTeam] = useUpdateTeamMutation();

  const teamsData = useMemo(() => {
    return transformTeamsData(response);
  }, [response]);

  const mapTeamDataToRequest = useCallback(
    (teamFormData: Partial<TeamData>): TeamRequestData =>
      mapTeamDataToRequestUtil(teamFormData as TeamData),
    []
  );

  const handleUpdate = useCallback(
    async (formData: Partial<TeamData>) => {
      await updateTeam({
        ...mapTeamDataToRequest(formData),
        id: currentItemId
      })
        .unwrap()
        .then((payload) => {
          // eslint-disable-next-line no-console
          console.log('fulfilled', payload);
          if (!payload.data) throw new Error('Permission denied');
          setSubmitMessage('Team updated');
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('rejected', error);
          setSubmitMessage(`Couldn't update Team - ${error}`);
        });
    },
    [updateTeam, currentItemId, mapTeamDataToRequest, setSubmitMessage]
  );

  const handleCreate = useCallback(
    async (formData: Partial<TeamData>) => {
      await createTeam(mapTeamDataToRequest(formData))
        .unwrap()
        .then((payload) => {
          // eslint-disable-next-line no-console
          console.log('fulfilled', payload);
          if (!payload.data) throw new Error('Permission denied');
          setSubmitMessage('Team created');
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('rejected', error);
          setSubmitMessage(`Couldn't create Team - ${error}`);
        });
    },
    [createTeam, mapTeamDataToRequest, setSubmitMessage]
  );

  return {
    createTeam: handleCreate,
    isError,
    isLoading,
    isSuccess,
    mapTeamDataToRequest,
    meta: response?.meta || {},
    page: response?.meta?.current_page || 0,
    teams: teamsData || [],
    totalPages: response?.meta?.total_pages || 0,
    updateTeam: handleUpdate
  };
};
